import { PreloadedQuery, usePreloadedQuery } from 'react-relay';
import { AttemptsWithoutPassingQuery } from '../../graphQLOperations/__generated__/AttemptsWithoutPassingQuery.graphql';
import { attemptsWithoutPassing } from '../../graphQLOperations/AttemptsWithoutPassing';
import { Bell } from 'lucide-react';
import { Paginator, PaginatorPageChangeEvent } from 'primereact/paginator';
import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import CopyListOfStudentsButton from './CopyListOfStudentsButton';
import { MaskedStudentData } from '../MaskedData/MaskedStudentData';
import { PiiTypes } from '../../analytics/dataMasking';
import { StudentNameLink } from '../common/StudentNameLink/StudentNameLink';
import { useAppSelector } from '../../redux/hooks';
import { selectedTeacherDashboardFilter } from '../../redux/reducers/teacherDashboardFiltersSlice';

interface Props {
  queryReference: PreloadedQuery<AttemptsWithoutPassingQuery, Record<string, unknown>>;
  reportFromDate: Date;
  reportToDate: Date;
}

interface StudentData {
  id: string;
  firstName: string;
  lastName: string;
  publicUserId: string;
}

interface StudentAlert {
  student: StudentData;
  attempts: number;
}

const PAGE_SIZE = 5;

// The PrimeReact theme renders the page links as borderless blue circles, so the chevrons are boxed
// here: a neutral rounded square on either side of the page count.
const PAGE_LINK_CLASS_NAME =
  '!tw-h-9 !tw-w-9 !tw-min-w-0 !tw-rounded-lg tw-border tw-border-solid tw-border-neutral-200 !tw-text-neutral-950';

const sortByLastNameThenFirstName = (a: StudentData, b: StudentData) =>
  (a.lastName || '').localeCompare(b.lastName || '', undefined, { sensitivity: 'base' }) ||
  (a.firstName || '').localeCompare(b.firstName || '', undefined, { sensitivity: 'base' });

// Most attempts first: this is an alert list, so the students furthest from passing lead. Ties fall
// back to the name order so the list stays stable between renders of the same data.
const byMostAttemptsThenName = (a: StudentAlert, b: StudentAlert) =>
  b.attempts - a.attempts || sortByLastNameThenFirstName(a.student, b.student);

const SkillChallengeAttemptsWithoutPassing = ({ queryReference, reportFromDate, reportToDate }: Props) => {
  const [first, setFirst] = useState(0);
  const teacherDashboardFilters = useAppSelector(selectedTeacherDashboardFilter);
  const { studentsAttemptedSkillsWithoutPassing } = usePreloadedQuery<AttemptsWithoutPassingQuery>(
    attemptsWithoutPassing,
    queryReference,
  );

  const sortedAlerts = studentsAttemptedSkillsWithoutPassing
    .filter((entry): entry is StudentAlert => entry.student !== null)
    .sort(byMostAttemptsThenName);

  const onPageChange = (event: PaginatorPageChangeEvent) => {
    setFirst(event.first);
  };

  useEffect(() => {
    setFirst(0);
  }, [queryReference]);

  return (
    <div
      id="ThreePlusWeekly"
      className="tw-flex tw-h-full tw-w-full tw-flex-col tw-rounded-2xl tw-border-2 tw-border-solid tw-border-neutral-100 tw-bg-neutral-0 tw-p-4"
    >
      <div className="tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-3 tw-border-x-0 tw-border-t-0 tw-border-b-2 tw-border-solid tw-border-neutral-100 tw-pb-3">
        <h2 className="tw-m-0 tw-font-sora tw-text-xl tw-font-bold tw-not-italic tw-text-neutral-950">Alerts</h2>
        <p className="tw-m-0 tw-font-atkinson tw-text-base tw-text-neutral-600">
          {format(reportFromDate, 'P')} - {format(reportToDate, 'P')}
        </p>
      </div>

      {sortedAlerts.length ? (
        <>
          {/* Labels the list, so it goes with the list: with no alerts there is nothing for it to
              describe and it would read as a heading over the all-clear state. */}
          <p className="tw-mb-0 tw-mt-4 tw-font-atkinson tw-text-base tw-font-bold tw-text-neutral-950">
            3+ Skill Challenge <span className="tw-text-red-600">attempts without passing</span>
          </p>

          <ul className="tw-m-0 tw-flex tw-flex-1 tw-list-none tw-flex-col tw-gap-4 tw-p-0 tw-pt-4">
            {sortedAlerts.slice(first, PAGE_SIZE + first).map(({ student, attempts }) => (
              <li key={student.publicUserId} className="tw-flex tw-items-center tw-justify-between tw-gap-3">
                <StudentNameLink
                  studentId={student.id}
                  classIds={teacherDashboardFilters?.classes?.map(c => c.id)}
                  className="tw-font-atkinson tw-text-base tw-font-bold"
                  // Sent as the `source` property on `navigated_to_isr`, not via autocapture: the
                  // link text is a student's name, so the component stays excluded from autocapture.
                  // Matches the sibling copy button's id so both halves of the widget share a name.
                  id="3plusSkillChallengeStudentNameLink"
                >
                  <MaskedStudentData
                    type={PiiTypes.FullName}
                    student={student}
                  >{`${student.firstName} ${student.lastName}`}</MaskedStudentData>
                </StudentNameLink>
                <p className="tw-m-0 tw-whitespace-nowrap tw-font-atkinson tw-text-base tw-text-neutral-600">
                  <span className="tw-font-bold tw-text-neutral-950">{attempts}</span> attempts
                </p>
              </li>
            ))}
          </ul>

          {sortedAlerts.length > PAGE_SIZE && (
            <div className="tw-flex tw-items-center tw-justify-center">
              <Paginator
                first={first}
                rows={PAGE_SIZE}
                totalRecords={sortedAlerts.length}
                onPageChange={onPageChange}
                template="PrevPageLink CurrentPageReport NextPageLink"
                prevPageLinkIcon={<i className="nc_icon-nc_left icon-sm"></i>}
                nextPageLinkIcon={<i className="nc_icon-nc_right icon-sm"></i>}
                // Pages, not students: the teacher reads how far through the list they are, and the
                // per-student counts are already on each row.
                currentPageReportTemplate="{currentPage} of {totalPages}"
                className="tw-bg-transparent tw-p-0"
                pt={{
                  prevPageButton: { className: PAGE_LINK_CLASS_NAME },
                  nextPageButton: { className: PAGE_LINK_CLASS_NAME },
                  current: { className: 'tw-mx-3 tw-font-atkinson tw-text-base tw-text-neutral-950' },
                }}
              />
            </div>
          )}

          <div className="tw-mt-4 tw-flex tw-justify-end tw-border-x-0 tw-border-b-0 tw-border-t-2 tw-border-solid tw-border-neutral-100 tw-pt-3">
            <CopyListOfStudentsButton
              studentList={sortedAlerts.map(alert => alert.student)}
              label="Copy Student List"
              id="3plusSkillChallengeCopyButton"
            />
          </div>
        </>
      ) : (
        // Same badge/title/subtitle shape as TeacherDashboardWidgetEmpty, inline so the card keeps its
        // own header and date range. The `!tw-rotate-34` those copies carry is dropped: no `rotate`
        // scale is configured, so it generates nothing and the icon was always upright anyway.
        <div className="tw-flex tw-flex-1 tw-flex-col tw-items-center tw-justify-center tw-py-6">
          <span className="tw-flex tw-rounded-full tw-bg-stone-50 tw-p-4">
            <Bell size={30} className="tw-text-stone-500" />
          </span>
          <p className="tw-mb-2 tw-font-atkinson tw-text-2xl tw-font-bold tw-text-gray-600">No alerts</p>
          <p className="tw-m-0 tw-font-atkinson tw-text-sm tw-text-gray-500">Nothing to worry about!</p>
        </div>
      )}
    </div>
  );
};

export default SkillChallengeAttemptsWithoutPassing;
